import { DateOnly } from '@if/core/date'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  useFormDialog,
} from '@if/mui'
import { getToday } from '@src/services/DateTimeProvider'
import { useTranslatedFeeTypeList } from '@src/types/constants'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { CirclePlus } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AddFeeSchemaType, getAddFeeSchema } from './AddFee-schema'

interface UseAddFeeDialogProps {
  /** The earliest date a fee can be billed on; the latest is today. */
  activationDate: DateOnly
  isLockedByOther: boolean
  /** Recomputes the calendar with the fee; the dialog shows its loading state until it settles, and stays open on error. */
  onSubmit: (fee: AddFeeSchemaType) => Promise<SignedCalendarSimulation>
}

/** Collects one fee and resolves with the calendar recomputed with it. */
export const useAddFeeDialog = ({ activationDate, isLockedByOther, onSubmit }: UseAddFeeDialogProps) => {
  const { t } = useTranslation()
  const feeTypeOptions = useTranslatedFeeTypeList()
  const schema = useMemo(() => getAddFeeSchema(activationDate, getToday()), [activationDate])

  return useFormDialog<AddFeeSchemaType, void, SignedCalendarSimulation>({
    icon: CirclePlus,
    title: t('loan.feesDialog.addFee'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    successMessage: false,
    onSubmit,
    confirmDisabled: () => isLockedByOther,
    confirmTooltip: () => (isLockedByOther ? t('common.lockTooltip.lockedByOther') : null),
    content: ({ form }) => (
      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledSelect
          control={form.control}
          name="feeType"
          options={feeTypeOptions}
          label={t('loan.feesDialog.feeType')}
          disableEmptyOption
          disabled={isLockedByOther}
        />
        <ControlledNumberField
          control={form.control}
          name="amount"
          isMoney
          label={t('loan.feesDialog.amount')}
          disabled={isLockedByOther}
        />
        <ControlledDateOnlyPicker
          control={form.control}
          name="effectiveDate"
          label={t('loan.feesDialog.effectiveDate')}
          minDate={activationDate}
          maxDate={getToday()}
          disabled={isLockedByOther}
        />
        <ControlledTextField
          control={form.control}
          name="note"
          label={t('loan.feesDialog.note')}
          span="full"
          disabled={isLockedByOther}
        />
      </FieldGrid>
    ),
  })
}
